import { Dispatch, useEffect, useRef, useState } from "react";
import { nanoid } from "nanoid";
import { Unsubscribe } from "firebase/firestore";
import { AssistantCreativityLevel, CoursableProject, ProjectMessage } from "../../../../backend/Projects/types";
import { FetchProjectMessages, StreamProjectMessage } from "../../../../backend/Projects/ProjectMessages";
import { ProjectStreamMessageCreated } from "../../../../backend/Projects/ProjectStream";
import { ResetProjectChat, SubscribeToProjectChanges } from "../../../../backend/Projects/ProjectBase";
import useCancellablePromise from "../../../../utils/Hooks/useCancellablePromise";
import { useNotifications } from "../../../../utils/NotificationsContext";
import { AttachAttachment, MessageAttachment } from "../Chat/MessageAttachments";

const placeholderMessageID = "generating-response";

const useProjectMessages = (project: CoursableProject | undefined, setProject: Dispatch<React.SetStateAction<CoursableProject | undefined>>) => {
  const [resettingChat, setResettingChat] = useState(false);
  const [messages, setMessages] = useState<ProjectMessage[]>([]);
  const streamingMessageID = useRef<string | undefined>(undefined);

  const { cancellablePromise } = useCancellablePromise(false);
  const { sendError } = useNotifications();
  const creativityLevel = useRef<AssistantCreativityLevel>("regular");

  const sendMessageAbortController = useRef<AbortController | undefined>(undefined);
  const messageGenerationListener = useRef<Unsubscribe | undefined>(undefined);
  const initializingProjectID = useRef<string | undefined>(undefined);

  async function Initialize(project: CoursableProject) {
    setMessages([]);

    try {
      initializingProjectID.current = project.id;
      const fetchedMessages = (await FetchProjectMessages(project.id)).filter((message) => message.sender !== "assistant" || message.content !== "");
      if (initializingProjectID.current !== project.id) return;

      setMessages(fetchedMessages);

      if (project.isGenerating) {
        AddPlaceholderMessage();
        WaitForGenerationEnd(project);
      }
    } catch (error: any) {
      if (error.name === "AbortError" || error.isCanceled) return;
      console.log(error);
      sendError();
    }
  }

  async function SendMessage(message: string, attachments?: MessageAttachment[]) {
    if (!project) return;

    StopStream();
    const abortController = new AbortController();
    sendMessageAbortController.current = abortController;

    const content =
      attachments?.slice().reverse().reduce((previous, attachment) => AttachAttachment(previous, attachment), message) ?? message;
    const userMessage: ProjectMessage = {
      id: `local-${nanoid()}`,
      content,
      sender: "user",
      sentAt: new Date(),
      annotations: [],
    };

    AddMessage(userMessage);
    AddPlaceholderMessage();
    ToggleIsGenerating(true);

    try {
      await StreamProjectMessage(
        project.id,
        content,
        creativityLevel.current,
        {
          onMessageCreated: OnStreamMessageCreated,
          onMessageDelta: OnStreamMessageDelta,
          onMessageCompleted: OnStreamMessageCompleted,
        },
        abortController.signal
      );
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.log(error);
        sendError("Failed to send your message.", error.message || "Please try again later.");
      }
      RemoveEmptyStreamingMessage();
    } finally {
      if (sendMessageAbortController.current === abortController) {
        sendMessageAbortController.current = undefined;
        streamingMessageID.current = undefined;
        ToggleIsGenerating(false);
      }
    }
  }

  function StopStream() {
    if (!sendMessageAbortController.current) return;
    sendMessageAbortController.current.abort();
    sendMessageAbortController.current = undefined;
    RemoveEmptyStreamingMessage();
    streamingMessageID.current = undefined;
    ToggleIsGenerating(false);
  }

  function AddMessage(message: ProjectMessage) {
    setMessages((previous) => [message, ...previous]);
  }

  function AddPlaceholderMessage() {
    streamingMessageID.current = placeholderMessageID;
    AddMessage({
      id: placeholderMessageID,
      content: "",
      sender: "assistant",
      sentAt: new Date(),
      annotations: [],
    });
  }

  function RemoveEmptyStreamingMessage() {
    const activeMessageID = streamingMessageID.current;
    if (!activeMessageID) return;
    setMessages((previous) => previous.filter((message) => message.id !== activeMessageID || message.content.length > 0));
  }

  function OnStreamMessageCreated(event: ProjectStreamMessageCreated) {
    const previousMessageID = streamingMessageID.current ?? placeholderMessageID;
    streamingMessageID.current = event.messageID;
    setMessages((previous) =>
      previous.map((message) =>
        message.id === previousMessageID
          ? {
              ...message,
              id: event.messageID,
            }
          : message
      )
    );

    if (event.conversationID) {
      setProject((previous) => (previous ? { ...previous, chatConversationID: event.conversationID } : previous));
    }
  }

  function OnStreamMessageDelta(delta: string) {
    const activeMessageID = streamingMessageID.current;
    if (!activeMessageID) return;
    setMessages((previous) => previous.map((message) => (message.id === activeMessageID ? { ...message, content: message.content + delta } : message)));
  }

  function OnStreamMessageCompleted(completedMessage: ProjectMessage) {
    const activeMessageID = streamingMessageID.current;
    setMessages((previous) => {
      const messageIndex = previous.findIndex((message) => message.id === activeMessageID || message.id === placeholderMessageID);
      if (messageIndex === -1) return [completedMessage, ...previous];
      return previous.map((message, index) => (index === messageIndex ? completedMessage : message));
    });
    streamingMessageID.current = completedMessage.id;
  }

  function ToggleIsGenerating(isGenerating: boolean) {
    setProject((previous) => (previous ? { ...previous, isGenerating } : previous));
  }

  function WaitForGenerationEnd(currentProject: CoursableProject) {
    messageGenerationListener.current?.();
    messageGenerationListener.current = SubscribeToProjectChanges(currentProject.id, async (updatedProject) => {
      if (updatedProject.isGenerating) return;
      messageGenerationListener.current?.();
      setProject(updatedProject);
      const fetchedMessages = await cancellablePromise(FetchProjectMessages(updatedProject.id));
      setMessages(fetchedMessages);
    });
  }

  async function ResetChat() {
    if (!project) return;

    StopStream();
    setResettingChat(true);

    try {
      const chatConversationID = await ResetProjectChat(project.id);
      setMessages([]);
      setProject((previous) => (previous ? { ...previous, chatConversationID, isGenerating: false } : previous));
    } catch (error) {
      console.error(error);
      sendError("Oops, failed resetting this chat. Please try again.");
    } finally {
      setResettingChat(false);
    }
  }

  useEffect(() => {
    return () => {
      messageGenerationListener.current?.();
      const abortController = sendMessageAbortController.current;
      sendMessageAbortController.current = undefined;
      streamingMessageID.current = undefined;
      abortController?.abort();
    };
  }, [project?.id]);

  return {
    creativityLevel,
    messages,
    streamingMessageID: streamingMessageID.current,
    resettingChat,
    SendMessage,
    StopStream,
    ResetChat,
    Initialize,
  };
};

export default useProjectMessages;
